import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';

export type PresenceStatus = 'active' | 'oncall' | 'busy' | 'dnd' | 'offline';

/** Color y etiqueta i18n de cada estado del punto del avatar. */
export const PRESENCE_UI: Record<PresenceStatus, { color: string; key: string }> = {
  active: { color: '#16a34a', key: 'pres.dotActive' },
  oncall: { color: '#2563eb', key: 'pres.dotOnCall' },
  busy: { color: '#f59e0b', key: 'pres.dotBusy' },
  dnd: { color: '#dc2626', key: 'pres.dotDnd' },
  offline: { color: '#9ca3af', key: 'pres.dotOffline' },
};
type PresenceRow = { userId: string; status: PresenceStatus; updatedAt: string | null };

const PresenceCtx = createContext<Map<string, PresenceStatus>>(new Map());

/**
 * Punto rojo/verde en el avatar (feedback 2026-07-30): pollea /api/presence cada 20s y expone un
 * Map userId→status. Un solo poller para toda la app (montado en AppShell) en vez de uno por
 * avatar renderizado.
 */
export function PresenceProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Map<string, PresenceStatus>>(new Map());

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { rows } = await api.get<{ rows: PresenceRow[] }>('/api/presence');
        if (!alive) return;
        // Solo si cambió algo: un Map nuevo cada 20 s repintaba todos los avatares visibles.
        setMap((prev) => {
          if (prev.size === rows.length && rows.every((r) => prev.get(r.userId) === r.status)) return prev;
          return new Map(rows.map((r) => [r.userId, r.status]));
        });
      } catch { /* best-effort */ }
    }
    load();
    // Con la pestaña oculta no se consulta (auditoría 2026-09-15: era el poller más frecuente de
    // prod, 49 veces por minuto, la mitad desde pestañas que nadie miraba); al volver, refresca.
    const id = setInterval(() => { if (!document.hidden) void load(); }, 20_000);
    const alVolver = () => { if (!document.hidden) void load(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', alVolver); };
  }, []);

  return <PresenceCtx.Provider value={map}>{children}</PresenceCtx.Provider>;
}

/** undefined = todavía no le conocemos la extensión (no se pinta punto). */
export function usePresenceStatus(userId: string | null | undefined): PresenceStatus | undefined {
  const map = useContext(PresenceCtx);
  return userId ? map.get(userId) : undefined;
}
